// ============================================================
// SALT  - Team Profile Page
// Route: /teams/:teamId
// Three tabs: Overview (summary + roster + recent), Match History, Management
// ============================================================

import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import axiosClient from '../api/axiosClient';
import { useAuthContext } from '../context/AuthContext';
import { useGame } from '../hooks/useGame';
import { useToast } from '../hooks/useToast';
import SeasonHeader from '../components/dashboard/SeasonHeader';
import ToastContainer from '../components/ui/Toast';

import '../styles/user-profile.css';
import '../styles/team-profile.css';
import '../styles/report.css';
import ReportButton from '../components/ui/ReportButton';
import { extractError } from '../utils/formatters';

// ============================================================
// Types (mirrors v2 backend response shapes)
// ============================================================

type TeamRole = 'captain' | 'member';
type MatchType = 'available_now' | 'scheduled' | 'direct_challenge';

interface TeamProfileHeader {
  teamId: number;
  teamName: string;
  teamTag: string | null;
  logoUrl: string | null;
  logoHidden: boolean;
  bannerUrl: string | null;
  description: string | null;
  gameId: number;
  gameName: string;
  gameCode: string;
  seasonId: number;
  seasonName: string;
  status: 'active' | 'deleted';
  createdAt: string;
}

interface TeamSummary {
  record: { wins: number; losses: number };
  matchesPlayed: number;
  points: number;
  rank: number | null;
}

interface RosterMember {
  userId: number;
  username: string;
  avatarUrl: string | null;
  ign: string | null;
  role: TeamRole;
  joinedAt: string;
  profileUrl: string;
}

interface TeamRecentMatch {
  matchId: number;
  opponentTeamId: number;
  opponentTeamName: string;
  opponentTeamLogo: string | null;
  result: 'win' | 'loss';
  score: string;
  completedAt: string;
  matchUrl: string;
}

interface ChallengeInfo {
  canChallenge: boolean;
  reason?: string;
  challengeUrl?: string;
  viewerTeamId?: number;
}

interface TeamProfileOverviewResponse {
  header: TeamProfileHeader;
  summary: TeamSummary;
  roster: RosterMember[];
  recentMatches: TeamRecentMatch[];
  challengeInfo: ChallengeInfo | null;
}

interface TeamMatchHistoryEntry {
  matchId: number;
  when: string;
  opponent: {
    teamId: number;
    teamName: string;
    teamLogo: string | null;
    teamUrl: string;
  };
  result: 'win' | 'loss';
  score: string;
  matchType: MatchType;
  matchUrl: string;
}

interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface TeamMatchHistoryResponse {
  matches: TeamMatchHistoryEntry[];
  pagination: PaginationMeta;
}

interface PendingInvite {
  inviteId: number;
  invitee: {
    userId: number;
    username: string;
    avatarUrl: string | null;
  };
  inviter: {
    userId: number;
    username: string;
  };
  sentAt: string;
}

interface ManageableRosterMember {
  userId: number;
  username: string;
  avatarUrl: string | null;
  ign: string | null;
  role: TeamRole;
  joinedAt: string;
  profileUrl: string;
  canKick: boolean;
}

interface TeamManagementResponse {
  teamId: number;
  teamName: string;
  viewerRole: TeamRole;
  isCaptain: boolean;
  canChangeName: boolean;
  nameChangeAvailableAt: string | null;
  roster: ManageableRosterMember[];
  pendingInvites: PendingInvite[];
  availableSlots: number;
  maxTeamSize: number;
}

// ============================================================
// Constants
// ============================================================

const TABS = {
  OVERVIEW: 'overview',
  MATCHES: 'matches',
  MANAGEMENT: 'management',
} as const;

type TabKey = (typeof TABS)[keyof typeof TABS];

const MATCH_HISTORY_PAGE_SIZE = 20;

// ============================================================
// Helpers
// ============================================================

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function formatDateFull(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
}

function getInitials(name: string): string {
  return (name || 'T')
    .split(' ')
    .map((p) => p[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function stripControlChars(raw: string): string {
  return raw
    .split('')
    .filter((ch) => {
      const code = ch.charCodeAt(0);
      return code >= 32 && code !== 127;
    })
    .join('');
}

// ============================================================
// Component
// ============================================================

const TeamProfilePage: React.FC = () => {
  const { user, isAuthenticated } = useAuthContext();
  const { gameId, gameCode: currentGameCode, season, setSeason, isLoading: seasonLoading } = useGame();
  const navigate = useNavigate();
  const { teamId: routeTeamId } = useParams<{ teamId: string }>();
  const toast = useToast();

  // ---- Core state ----
  const [profile, setProfile] = useState<TeamProfileOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<TabKey>(TABS.OVERVIEW);

  // ---- Match History tab state ----
  const [matchHistory, setMatchHistory] = useState<TeamMatchHistoryEntry[]>([]);
  const [matchPagination, setMatchPagination] = useState<PaginationMeta | null>(null);
  const [matchHistoryLoading, setMatchHistoryLoading] = useState(false);
  const [matchHistoryPage, setMatchHistoryPage] = useState(1);

  // ---- Management tab state ----
  const [management, setManagement] = useState<TeamManagementResponse | null>(null);
  const [managementLoading, setManagementLoading] = useState(false);
  const [managementError, setManagementError] = useState('');

  // ---- Action states ----
  const [actionLoading, setActionLoading] = useState(false);

  // Invite player
  const [inviteUsername, setInviteUsername] = useState('');
  const [inviteSending, setInviteSending] = useState(false);

  // Invite autocomplete
  const [inviteResults, setInviteResults] = useState<{ userId: number; username: string; avatarUrl: string | null }[]>([]);
  const [inviteSearchLoading, setInviteSearchLoading] = useState(false);
  const [inviteSelectedIdx, setInviteSelectedIdx] = useState(-1);
  const [inviteDropdownOpen, setInviteDropdownOpen] = useState(false);
  const inviteDropdownRef = useRef<HTMLDivElement>(null);
  const inviteInputRef = useRef<HTMLInputElement>(null);

  // Kick
  const [kickConfirm, setKickConfirm] = useState<{ userId: number; username: string } | null>(null);

  // Transfer captain
  const [transferTargetId, setTransferTargetId] = useState('');
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

  // Change team name
  const [teamNameDraft, setTeamNameDraft] = useState('');
  const [isTeamNameModalOpen, setIsTeamNameModalOpen] = useState(false);

  // Leave team
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);

  // Edit description
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);

  // Edit tag
  const [tagDraft, setTagDraft] = useState('');
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);

  // Cancel invite
  const [cancelingInviteId, setCancelingInviteId] = useState<number | null>(null);

  // -- Logo upload state --
  const [isLogoModalOpen, setIsLogoModalOpen] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // ---- Derived ----
  const numericTeamId = useMemo(() => {
    const n = Number(routeTeamId);
    return Number.isNaN(n) ? null : n;
  }, [routeTeamId]);

  const header = profile?.header ?? null;
  const summary = profile?.summary ?? null;
  const roster = useMemo(() => profile?.roster ?? [], [profile]);
  const recentMatches = profile?.recentMatches ?? [];
  const challengeInfo = profile?.challengeInfo ?? null;

  const teamName = header?.teamName ?? 'Team';
  const teamInitials = getInitials(teamName);
  const gameSeasonLabel = header
    ? `${header.gameName} \u2022 ${header.seasonName}`
    : '';
  const isArchived = header?.status === 'deleted';
  const isViewerCaptain = useMemo(() => {
    if (!user || !roster.length) return false;
    const viewer = roster.find((m) => m.userId === user.userId);
    return viewer?.role === 'captain';
  }, [user, roster]);

  // ---- Fetch team profile ----
  const fetchProfile = useCallback(async () => {
    if (!numericTeamId) {
      setError('Invalid team ID.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const res = await axiosClient.get<TeamProfileOverviewResponse>(
        `/teams/${numericTeamId}/profile`
      );
      const data = (res.data as any)?.data ?? res.data;
      setProfile(data);
      setTeamNameDraft(data.header?.teamName ?? '');
    } catch (err: any) {
      const msg = extractError(err);
      setError(msg);
      toast.error('Error', msg);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numericTeamId]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Redirect to correct subdomain if team belongs to a different game
  useEffect(() => {
    const teamGameCode = header?.gameCode;
    if (!teamGameCode || !currentGameCode) return;
    if (teamGameCode.toLowerCase() === currentGameCode.toLowerCase()) return;

    // Validate gameCode against allowlist before redirecting to subdomain
    const VALID_GAME_CODES = ['hi', 'h3'];
    if (!VALID_GAME_CODES.includes(teamGameCode.toLowerCase())) return;

    // Build the correct subdomain URL
    const { protocol, port, pathname, search, hash } = window.location;
    const hostname = window.location.hostname;

    let newHostname: string;
    // Production: {code}.playsalt.gg
    if (hostname.endsWith('.playsalt.gg') || hostname === 'playsalt.gg') {
      newHostname = `${teamGameCode.toLowerCase()}.playsalt.gg`;
    }
    // Local dev: {code}.localhost
    else if (hostname.includes('.localhost') || hostname === 'localhost') {
      newHostname = `${teamGameCode.toLowerCase()}.localhost`;
    } else {
      return; // Unknown hostname pattern, don't redirect
    }

    const portSuffix = port ? `:${port}` : '';
    const newUrl = `${protocol}//${newHostname}${portSuffix}${pathname}${search}${hash}`;
    window.location.replace(newUrl);
  }, [header?.gameCode, currentGameCode]);


  // Fetch season header if GameContext does not have it yet (e.g. direct navigation)
  useEffect(() => {
    if (season || !gameId) return;
    let cancelled = false;
    axiosClient
      .get(`/seasons/current?game_id=${gameId}`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data ?? res.data;
        if (data) setSeason(data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [season, gameId, setSeason]);
  // ---- Fetch match history (paginated) ----
  const fetchMatchHistory = useCallback(
    async (page: number) => {
      if (!numericTeamId) return;
      try {
        setMatchHistoryLoading(true);
        const res = await axiosClient.get<TeamMatchHistoryResponse>(
          `/teams/${numericTeamId}/match-history`,
          { params: { page, limit: MATCH_HISTORY_PAGE_SIZE } }
        );
        const data = (res.data as any)?.data ?? res.data;
        setMatchHistory(data.matches ?? []);
        setMatchPagination(data.pagination ?? null);
        setMatchHistoryPage(page);
      } catch (err: any) {
        toast.error('Error', extractError(err));
      } finally {
        setMatchHistoryLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [numericTeamId]
  );

  // ---- Fetch management data ----
  const fetchManagement = useCallback(async () => {
    if (!numericTeamId || !isAuthenticated) return;
    try {
      setManagementLoading(true);
      setManagementError('');
      const res = await axiosClient.get<TeamManagementResponse>(
        `/teams/${numericTeamId}/management`
      );
      const data = (res.data as any)?.data ?? res.data;
      setManagement(data);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setManagementError('not_member');
      } else {
        setManagementError(extractError(err));
      }
    } finally {
      setManagementLoading(false);
    }
  }, [numericTeamId, isAuthenticated]);

  // ---- Load tab data on switch ----
  useEffect(() => {
    if (activeTab === TABS.MATCHES && matchHistory.length === 0 && !matchHistoryLoading) {
      fetchMatchHistory(1);
    }
    if (activeTab === TABS.MANAGEMENT && !management && !managementLoading) {
      fetchManagement();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // ---- Tab switching ----
  const handleTabChange = (tab: string) => {
    setActiveTab(tab as TabKey);
  };

  const renderTabButton = (tab: TabKey, label: string) => (
    <button
      key={tab}
      type="button"
      className={`sl-profile-tab ${activeTab === tab ? 'sl-profile-tab--active' : ''}`}
      onClick={() => handleTabChange(tab)}
    >
      {label}
    </button>
  );

  // ============================================================
  // Invite Autocomplete Effects (must be after numericTeamId)
  // ============================================================

  // Debounced autocomplete search
  useEffect(() => {
    const query = inviteUsername.trim();

    // Clear results if query too short
    if (query.length < 2) {
      setInviteResults([]);
      setInviteDropdownOpen(false);
      setInviteSelectedIdx(-1);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setInviteSearchLoading(true);
        const params: Record<string, string> = { q: query, limit: '8' };
        if (numericTeamId) params.teamId = String(numericTeamId);
        if (gameId) params.gameId = String(gameId);
        const res = await axiosClient.get('/users/autocomplete', { params });
        const users = res.data?.data?.users || [];
        setInviteResults(users);
        setInviteDropdownOpen(users.length > 0 || query.length >= 2);
        setInviteSelectedIdx(-1);
      } catch {
        setInviteResults([]);
        setInviteDropdownOpen(false);
      } finally {
        setInviteSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [inviteUsername, numericTeamId, gameId]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        inviteDropdownRef.current &&
        !inviteDropdownRef.current.contains(e.target as Node) &&
        inviteInputRef.current &&
        !inviteInputRef.current.contains(e.target as Node)
      ) {
        setInviteDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ============================================================
  // Captain Actions
  // ============================================================

  // Select a user from autocomplete dropdown
  const handleInviteSelect = (username: string) => {
    setInviteUsername(username);
    setInviteResults([]);
    setInviteDropdownOpen(false);
    setInviteSelectedIdx(-1);
  };

  // Invite Player
  const handleInvitePlayer = async () => {
    const username = inviteUsername.trim();
    if (!username || !numericTeamId) return;

    try {
      setInviteSending(true);
      await axiosClient.post(`/teams/${numericTeamId}/invites`, {
        invitee_username: username,
      });
      toast.success('Invite Sent', `Invited ${username} to the team.`);
      setInviteUsername('');
      setInviteResults([]);
      setInviteDropdownOpen(false);
      // Refresh management to show new pending invite
      fetchManagement();
    } catch (err: any) {
      toast.error('Invite Failed', extractError(err));
    } finally {
      setInviteSending(false);
    }
  };

  // ---- Kick Member ----
  const handleKickConfirm = async () => {
    if (!kickConfirm || !numericTeamId) return;
    try {
      setActionLoading(true);
      await axiosClient.delete(
        `/teams/${numericTeamId}/members/${kickConfirm.userId}`
      );
      toast.success('Player Removed', `${kickConfirm.username} has been removed from the team.`);
      setKickConfirm(null);
      // Refresh both profile and management
      fetchProfile();
      fetchManagement();
    } catch (err: any) {
      toast.error('Kick Failed', extractError(err));
    } finally {
      setActionLoading(false);
    }
  };

  // ---- Transfer Captain ----
  const handleTransferConfirm = async () => {
    if (!transferTargetId || !numericTeamId) return;
    try {
      setActionLoading(true);
      await axiosClient.post(`/teams/${numericTeamId}/transfer-captain`, {
        new_captain_user_id: Number(transferTargetId),
      });
      toast.success('Captain Transferred', 'The captain role has been transferred.');
      setIsTransferModalOpen(false);
      setTransferTargetId('');
      fetchProfile();
      fetchManagement();
    } catch (err: any) {
      toast.error('Transfer Failed', extractError(err));
    } finally {
      setActionLoading(false);
    }
  };

  // ---- Change Team Name ----
  const handleTeamNameConfirm = async () => {
    const newName = stripControlChars(teamNameDraft).trim();
    if (!newName || !numericTeamId) return;
    try {
      setActionLoading(true);
      await axiosClient.patch(`/teams/${numericTeamId}`, {
        team_name: newName,
      });
      toast.success('Name Changed', `Team name updated to ${newName}.`);
      setIsTeamNameModalOpen(false);
      fetchProfile();
      fetchManagement();
    } catch (err: any) {
      toast.error('Name Change Failed', extractError(err));
    } finally {
      setActionLoading(false);
    }
  };

  // ---- Cancel Invite ----
  const handleCancelInvite = async (inviteId: number) => {
    if (!numericTeamId) return;
    try {
      setCancelingInviteId(inviteId);
      await axiosClient.delete(`/teams/${numericTeamId}/invites/${inviteId}`);
      toast.success('Invite Canceled', 'The pending invite has been canceled.');
      fetchManagement();
    } catch (err: any) {
      toast.error('Cancel Failed', extractError(err));
    } finally {
      setCancelingInviteId(null);
    }
  };

  // ---- Leave Team ----
  const handleLeaveConfirm = async () => {
    if (!numericTeamId) return;
    try {
      setActionLoading(true);
      await axiosClient.post(`/teams/${numericTeamId}/leave`);
      toast.success('Left Team', 'You have left the team.');
      setIsLeaveModalOpen(false);
      fetchProfile();
      setManagement(null);
    } catch (err: any) {
      toast.error('Leave Failed', extractError(err));
    } finally {
      setActionLoading(false);
    }
  };

  // ---- Challenge Team ----
  const handleChallengeClick = () => {
    if (challengeInfo?.challengeUrl) {
      navigate(challengeInfo.challengeUrl);
    }
  };

  // -- Logo upload handlers -------------------------------------------
  const handleLogoClick = () => {
    if (!isViewerCaptain) return;
    setIsLogoModalOpen(true);
  };

  const handleLogoModalConfirm = () => {
    setIsLogoModalOpen(false);
    logoInputRef.current?.click();
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !numericTeamId) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Logo Error', 'Only JPEG, PNG, GIF, and WebP images are allowed.');
      return;
    }
    if (file.size > 1 * 1024 * 1024) {
      toast.error('Logo Error', 'Logo must be under 1 MB.');
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setLogoPreview(previewUrl);

    try {
      setLogoUploading(true);
      const formData = new FormData();
      formData.append('logo', file);

      const res = await axiosClient.post(
        `/teams/${numericTeamId}/logo`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const newLogoUrl = res.data?.data?.logo_url ?? res.data?.logo_url;

      setProfile((prev) =>
        prev
          ? { ...prev, header: { ...prev.header, logoUrl: newLogoUrl } }
          : prev
      );

      toast.success('Logo Updated', 'Team logo has been updated.');
    } catch (err: any) {
      console.error('[TeamProfile] Logo upload error:', err);
      toast.error(
        'Logo Error',
        err?.response?.data?.message || err?.response?.data?.error || 'Unable to upload logo.'
      );
      setLogoPreview(null);
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  // -- Edit Description -----------------------------------------------
  const openDescriptionModal = () => {
    setDescriptionDraft(header?.description ?? '');
    setIsDescriptionModalOpen(true);
  };

  const handleDescriptionConfirm = async () => {
    if (!numericTeamId) return;
    const newDesc = stripControlChars(descriptionDraft).trim();
    try {
      setActionLoading(true);
      await axiosClient.patch(`/teams/${numericTeamId}`, {
        description: newDesc || null,
      });
      toast.success('Profile Updated', 'Team description has been updated.');
      setIsDescriptionModalOpen(false);
      fetchProfile();
    } catch (err: any) {
      toast.error('Update Failed', extractError(err));
    } finally {
      setActionLoading(false);
    }
  };

  // -- Edit Tag --------------------------------------------------------
  const openTagModal = () => {
    setTagDraft(header?.teamTag ?? '');
    setIsTagModalOpen(true);
  };

  const handleTagConfirm = async () => {
    if (!numericTeamId) return;
    const newTag = stripControlChars(tagDraft).trim().toUpperCase();
    try {
      setActionLoading(true);
      await axiosClient.patch(`/teams/${numericTeamId}`, {
        team_tag: newTag || null,
      });
      toast.success('Tag Updated', newTag ? `Team tag set to [${newTag}].` : 'Team tag removed.');
      setIsTagModalOpen(false);
      fetchProfile();
    } catch (err: any) {
      toast.error('Update Failed', extractError(err));
    } finally {
      setActionLoading(false);
    }
  };

  // ============================================================
  // Tab Renderers
  // ============================================================

  // ---- Overview Tab ----
  const renderOverview = () => (
    <div className="sl-profile-section">
      <div className="sl-team-overview-grid">
        {/* Team Summary card */}
        <div className="sl-profile-card">
          <h3 className="sl-profile-card-title">Team Summary</h3>
          <p className="sl-profile-muted" style={{ marginBottom: '0.65rem' }}>
            Current season standings and stats for this team.
          </p>

          <div
            style={{
              padding: '0.75rem 0.9rem',
              borderRadius: '0.65rem',
              border: '1px solid rgba(163, 163, 163, 0.2)',
              background: 'rgba(18, 18, 18, 0.6)',
            }}
          >
            <div
              style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: '#e5e5e5',
                marginBottom: '0.55rem',
              }}
            >
              {gameSeasonLabel}
            </div>

            <div className="sl-profile-stat-row">
              <span className="sl-profile-stat-label">Record</span>
              <span className="sl-profile-stat-value">
                {summary ? `${summary.record.wins} - ${summary.record.losses}` : '0 - 0'}
              </span>
            </div>
            <div className="sl-profile-stat-row">
              <span className="sl-profile-stat-label">Matches Played</span>
              <span className="sl-profile-stat-value">{summary?.matchesPlayed ?? 0}</span>
            </div>
            <div className="sl-profile-stat-row">
              <span className="sl-profile-stat-label">Points</span>
              <span className="sl-profile-stat-value">
                {summary?.points != null ? summary.points.toFixed(2) : '0.00'}
              </span>
            </div>
            <div className="sl-profile-stat-row">
              <span className="sl-profile-stat-label">Rank</span>
              <span className="sl-profile-stat-value">
                {summary?.rank != null ? `#${summary.rank}` : 'Unranked'}
              </span>
            </div>
          </div>
        </div>

        {/* Recent Matches card */}
        <div className="sl-profile-card">
          <h3 className="sl-profile-card-title">Recent Matches</h3>

          {recentMatches.length === 0 ? (
            <p className="sl-team-empty">No matches played yet.</p>
          ) : (
            <div>
              {recentMatches.map((m) => (
                <div
                  key={m.matchId}
                  className="sl-team-match-row"
                  onClick={() => navigate(m.matchUrl)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(m.matchUrl)}
                >
                  <div className="sl-team-match-left">
                    <span className="sl-team-match-opponent">vs {m.opponentTeamName}</span>
                    <span className="sl-team-match-score">{m.score}</span>
                  </div>
                  <div className="sl-team-match-right">
                    <span className="sl-team-match-date">{formatDate(m.completedAt)}</span>
                    <span
                      className={`sl-team-match-result sl-team-match-result--${m.result}`}
                    >
                      {m.result.toUpperCase()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Roster section  - merged into overview */}
      <div className="sl-team-overview-roster">
        <div className="sl-profile-card">
          <h3 className="sl-profile-card-title">Roster</h3>

          {roster.length === 0 ? (
            <p className="sl-team-empty">No members found.</p>
          ) : (
            <table className="sl-team-roster-table">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>IGN</th>
                  <th>Role</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {roster.map((m) => (
                  <tr key={m.userId}>
                    <td>
                      <div
                        className="sl-team-roster-player"
                        onClick={() => navigate(m.profileUrl)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && navigate(m.profileUrl)}
                      >
                        <div className="sl-team-roster-avatar">
                          {m.avatarUrl ? (
                            <img src={m.avatarUrl} alt={m.username} />
                          ) : (
                            <span>{m.username[0]?.toUpperCase()}</span>
                          )}
                        </div>
                        <span>{m.username}</span>
                      </div>
                    </td>
                    <td style={{ color: m.ign ? '#e5e5e5' : '#737373', fontStyle: m.ign ? 'normal' : 'italic' }}>
                      {m.ign || 'Not set'}
                    </td>
                    <td>
                      <span
                        className={`sl-profile-pill ${
                          m.role === 'captain'
                            ? 'sl-profile-pill--captain'
                            : 'sl-profile-pill--member'
                        }`}
                      >
                        {m.role}
                      </span>
                    </td>
                    <td style={{ color: '#a3a3a3', fontSize: '0.82rem' }}>
                      {formatDateFull(m.joinedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );

  // ---- Match History Tab ----
  const renderMatchHistory = () => (
    <div className="sl-profile-section">
      <div className="sl-profile-card">
        <h3 className="sl-profile-card-title">All Matches</h3>

        {matchHistoryLoading && matchHistory.length === 0 ? (
          <div className="sl-team-loading">Loading match history...</div>
        ) : matchHistory.length === 0 ? (
          <p className="sl-team-empty">No completed matches found.</p>
        ) : (
          <>
            <div>
              {matchHistory.map((m) => (
                <div
                  key={m.matchId}
                  className="sl-team-match-row"
                  onClick={() => navigate(m.matchUrl)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(m.matchUrl)}
                >
                  <div className="sl-team-match-left">
                    <span className="sl-team-match-opponent">vs {m.opponent.teamName}</span>
                    <span className="sl-team-match-score">{m.score}</span>
                  </div>
                  <div className="sl-team-match-right">
                    <span className="sl-team-match-date">{formatDate(m.when)}</span>
                    <span
                      className={`sl-team-match-result sl-team-match-result--${m.result}`}
                    >
                      {m.result.toUpperCase()}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {matchPagination && matchPagination.totalPages > 1 && (
              <div className="sl-team-pagination">
                <button
                  type="button"
                  className="sl-team-pagination-btn"
                  disabled={!matchPagination.hasPrevPage || matchHistoryLoading}
                  onClick={() => fetchMatchHistory(matchHistoryPage - 1)}
                >
                  Previous
                </button>
                <span className="sl-team-pagination-info">
                  Page {matchPagination.page} of {matchPagination.totalPages}
                </span>
                <button
                  type="button"
                  className="sl-team-pagination-btn"
                  disabled={!matchPagination.hasNextPage || matchHistoryLoading}
                  onClick={() => fetchMatchHistory(matchHistoryPage + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  // ---- Management Tab ----
  const renderManagement = () => {
    // Not authenticated  - prompt login
    if (!isAuthenticated) {
      return (
        <div className="sl-team-mgmt-gate">
          <div className="sl-profile-card">
            <h3 className="sl-profile-card-title">Team Management</h3>
            <p className="sl-profile-muted">
              Log in to access team management features.
            </p>
          </div>
        </div>
      );
    }

    // Loading management data
    if (managementLoading && !management) {
      return (
        <div className="sl-profile-section">
          <div className="sl-team-loading">Loading management data...</div>
        </div>
      );
    }

    // Not a member  - show gate message
    if (managementError === 'not_member' || (!management && !managementLoading)) {
      return (
        <div className="sl-team-mgmt-gate">
          <div className="sl-profile-card">
            <h3 className="sl-profile-card-title">Team Management</h3>
            <p className="sl-profile-muted">
              Only team members can view management options. Join this team to access its settings.
            </p>
          </div>
        </div>
      );
    }

    // Error loading
    if (managementError && managementError !== 'not_member') {
      return (
        <div className="sl-profile-section">
          <p className="sl-profile-muted">{managementError}</p>
        </div>
      );
    }

    if (!management) return null;

    const mgmtRoster = management.roster;
    const isMgmtCaptain = management.isCaptain;
    const transferOptions = mgmtRoster.filter((m) => m.role !== 'captain');

    return (
      <div className="sl-profile-section">
        {/* Invite Player  - captain only */}
        {isMgmtCaptain && (
          <div className="sl-profile-card sl-team-mgmt-section sl-team-invite-card">
            <h3 className="sl-profile-card-title">Invite Player</h3>
            <p className="sl-profile-muted">
              Type a SALT username and send an invite.
              {management.availableSlots > 0
                ? ` ${management.availableSlots} slot${management.availableSlots === 1 ? '' : 's'} available.`
                : ' No slots available.'}
            </p>

            <div className="sl-team-invite-row">
              <div className="sl-team-invite-wrapper">
                <input
                  ref={inviteInputRef}
                  type="text"
                  className="sl-team-invite-input"
                  placeholder="Type a username..."
                  value={inviteUsername}
                  autoComplete="off"
                  onChange={(e) =>
                    setInviteUsername(stripControlChars(e.target.value).slice(0, 32))
                  }
                  onFocus={() => {
                    if (inviteResults.length > 0) setInviteDropdownOpen(true);
                  }}
                  onKeyDown={(e) => {
                    if (inviteDropdownOpen && inviteResults.length > 0) {
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setInviteSelectedIdx((prev) =>
                          prev < inviteResults.length - 1 ? prev + 1 : 0
                        );
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setInviteSelectedIdx((prev) =>
                          prev > 0 ? prev - 1 : inviteResults.length - 1
                        );
                      } else if (e.key === 'Enter' && inviteSelectedIdx >= 0) {
                        e.preventDefault();
                        handleInviteSelect(inviteResults[inviteSelectedIdx].username);
                      } else if (e.key === 'Escape') {
                        setInviteDropdownOpen(false);
                        setInviteSelectedIdx(-1);
                      } else if (e.key === 'Enter') {
                        handleInvitePlayer();
                      }
                    } else if (e.key === 'Enter') {
                      handleInvitePlayer();
                    }
                  }}
                  disabled={inviteSending || management.availableSlots === 0}
                />

                {/* Autocomplete dropdown */}
                {inviteDropdownOpen && (
                  <div className="sl-invite-dropdown" ref={inviteDropdownRef}>
                    {inviteSearchLoading ? (
                      <div className="sl-invite-dropdown-status">Searching...</div>
                    ) : inviteResults.length === 0 ? (
                      <div className="sl-invite-dropdown-status">
                        No users found matching "{inviteUsername.trim()}"
                      </div>
                    ) : (
                      inviteResults.map((user, idx) => (
                        <button
                          key={user.userId}
                          type="button"
                          className={`sl-invite-dropdown-item${idx === inviteSelectedIdx ? ' sl-invite-dropdown-item--active' : ''}`}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleInviteSelect(user.username);
                          }}
                          onMouseEnter={() => setInviteSelectedIdx(idx)}
                        >
                          {user.avatarUrl ? (
                            <img
                              src={user.avatarUrl}
                              alt=""
                              className="sl-invite-dropdown-avatar"
                            />
                          ) : (
                            <div className="sl-invite-dropdown-avatar sl-invite-dropdown-avatar--placeholder">
                              {user.username.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <span className="sl-invite-dropdown-username">{user.username}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              <Button
                variant="primary"
                size="sm"
                disabled={
                  inviteSending ||
                  !inviteUsername.trim() ||
                  management.availableSlots === 0
                }
                onClick={handleInvitePlayer}
              >
                {inviteSending ? 'Sending...' : 'Send Invite'}
              </Button>
            </div>
          </div>
        )}

        {/* Captain Tools  - captain only */}
        {isMgmtCaptain && (
          <div className="sl-profile-card sl-team-mgmt-section">
            <h3 className="sl-profile-card-title">Captain Tools</h3>
            <p className="sl-profile-muted">
              As the captain, you can transfer the captain role and manage your roster.
            </p>

            {/* Change Team Name */}
            <div className="sl-team-captain-tools-row" style={{ marginTop: '0.65rem' }}>
              <span className="sl-team-captain-tools-label">Change Team Name</span>
              <Button
                variant="primary"
                size="sm"
                disabled={!management.canChangeName || actionLoading}
                onClick={() => setIsTeamNameModalOpen(true)}
              >
                Change Name
              </Button>
            </div>
            {!management.canChangeName && management.nameChangeAvailableAt && (
              <p className="sl-profile-muted" style={{ fontSize: '0.78rem', marginTop: '0.25rem' }}>
                Name change available{' '}
                {formatDateFull(management.nameChangeAvailableAt)}.
              </p>
            )}

            {/* Transfer Captain */}
            <div className="sl-team-captain-tools-row" style={{ marginTop: '0.5rem' }}>
              <span className="sl-team-captain-tools-label">Transfer Captain Role</span>
              <div className="sl-team-captain-tools-actions">
                <select
                  className="sl-team-select"
                  value={transferTargetId}
                  onChange={(e) => setTransferTargetId(e.target.value)}
                  disabled={!transferOptions.length || actionLoading}
                >
                  <option value="">Select member...</option>
                  {transferOptions.map((m) => (
                    <option key={m.userId} value={m.userId}>
                      {m.username}
                    </option>
                  ))}
                </select>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!transferTargetId || actionLoading}
                  onClick={() => setIsTransferModalOpen(true)}
                >
                  Transfer
                </Button>
              </div>
            </div>
            {!transferOptions.length && (
              <p className="sl-profile-muted" style={{ fontSize: '0.78rem', marginTop: '0.25rem' }}>
                No other members to transfer captain to.
              </p>
            )}
          </div>
        )}

        {/* Roster (Manage)  - all members see roster, captain sees kick buttons */}
        <div className="sl-profile-card sl-team-mgmt-section">
          <h3 className="sl-profile-card-title">
            Roster {isMgmtCaptain ? '(Manage)' : ''}
          </h3>

          <table className="sl-team-roster-table">
            <thead>
              <tr>
                <th>Player</th>
                <th>IGN</th>
                <th>Role</th>
                <th>Joined</th>
                {isMgmtCaptain && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {mgmtRoster.map((m) => (
                <tr key={m.userId}>
                  <td>
                    <div
                      className="sl-team-roster-player"
                      onClick={() => navigate(m.profileUrl)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && navigate(m.profileUrl)}
                    >
                      <div className="sl-team-roster-avatar">
                        {m.avatarUrl ? (
                          <img src={m.avatarUrl} alt={m.username} />
                        ) : (
                          <span>{m.username[0]?.toUpperCase()}</span>
                        )}
                      </div>
                      <span>{m.username}</span>
                    </div>
                  </td>
                  <td style={{ color: m.ign ? '#e5e5e5' : '#737373', fontStyle: m.ign ? 'normal' : 'italic' }}>{m.ign || 'Not set'}</td>
                  <td>
                    <span
                      className={`sl-profile-pill ${
                        m.role === 'captain'
                          ? 'sl-profile-pill--captain'
                          : 'sl-profile-pill--member'
                      }`}
                    >
                      {m.role}
                    </span>
                  </td>
                  <td style={{ color: '#a3a3a3', fontSize: '0.82rem' }}>
                    {formatDateFull(m.joinedAt)}
                  </td>
                  {isMgmtCaptain && (
                    <td>
                      {m.canKick ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setKickConfirm({ userId: m.userId, username: m.username })
                          }
                          disabled={actionLoading}
                        >
                          Kick
                        </Button>
                      ) : null}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pending Invites  - captain only */}
        {isMgmtCaptain && (
          <div className="sl-profile-card sl-team-mgmt-section">
            <h3 className="sl-profile-card-title">Pending Invites</h3>

            {management.pendingInvites.length === 0 ? (
              <p className="sl-profile-muted">No pending invites for this team.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {management.pendingInvites.map((inv) => (
                  <div className="sl-profile-pending-row" key={inv.inviteId}>
                    <div className="sl-profile-pending-main">
                      <span className="sl-profile-pending-username">
                        {inv.invitee.username}
                      </span>{' '}
                      <span className="sl-profile-pending-meta">
                        &middot; Invited by {inv.inviter.username} &middot; {formatDate(inv.sentAt)}
                      </span>
                    </div>
                    <div className="sl-profile-pending-actions">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={cancelingInviteId === inv.inviteId}
                        onClick={() => handleCancelInvite(inv.inviteId)}
                      >
                        {cancelingInviteId === inv.inviteId ? 'Canceling...' : 'Cancel'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Leave Team  - all members */}
        <div className="sl-profile-card sl-team-mgmt-section sl-team-leave-card">
          <h3 className="sl-profile-card-title">Leave Team</h3>
          <p className="sl-profile-muted">
            Leaving the team will remove you from this roster. If you are the last remaining player,
            the team will be automatically removed from active play.
          </p>

          {isMgmtCaptain && mgmtRoster.length > 1 ? (
            <p className="sl-profile-muted" style={{ marginTop: '0.5rem' }}>
              You are currently the captain. Transfer the captain role to another member before
              leaving.
            </p>
          ) : (
            <div className="sl-profile-actions" style={{ marginTop: '0.65rem' }}>
              <Button
                variant="ghost"
                size="sm"
                disabled={actionLoading}
                onClick={() => setIsLeaveModalOpen(true)}
              >
                Leave Team
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ============================================================
  // Loading / Error States
  // ============================================================

  if (loading && !profile && !error) {
    return (
      <div className="sl-dashboard">
        <div className="sl-dashboard-inner sl-profile-page sl-team-profile-page">
          <p className="sl-profile-muted">Loading team profile...</p>
        </div>
      </div>
    );
  }

  if (!profile && !loading && error) {
    return (
      <div className="sl-dashboard">
        <div className="sl-dashboard-inner sl-profile-page sl-team-profile-page">
          <p className="sl-profile-muted" style={{ color: 'var(--sl-danger)' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!profile && !loading && !error) {
    return (
      <div className="sl-dashboard">
        <div className="sl-dashboard-inner sl-profile-page sl-team-profile-page">
          <p className="sl-profile-muted">This team profile could not be loaded.</p>
        </div>
      </div>
    );
  }

  // ============================================================
  // Main Render
  // ============================================================

  return (
    <>
      <div className="sl-dashboard">
        <div className="sl-dashboard-inner sl-profile-page sl-team-profile-page">
          {/* ---- Season Header ---- */}
          <SeasonHeader season={season} loading={seasonLoading} />

          {/* ---- Header Card ---- */}
          <section className="sl-card sl-profile-header-card">
            <div className="sl-profile-header-top">
              <div className="sl-profile-avatar-block">
                <div
                  className={`sl-profile-avatar-circle ${isViewerCaptain ? 'sl-profile-avatar-circle--editable' : ''}`}
                  onClick={handleLogoClick}
                  role={isViewerCaptain ? 'button' : undefined}
                  tabIndex={isViewerCaptain ? 0 : undefined}
                  aria-label={isViewerCaptain ? 'Change team logo' : undefined}
                >
                  {(logoPreview || (header?.logoUrl && !header?.logoHidden)) ? (
                    <img
                      className="sl-profile-avatar-img"
                      src={logoPreview || header!.logoUrl!}
                      alt={`${teamName} logo`}
                    />
                  ) : (
                    <span className="sl-profile-avatar-initials">{teamInitials}</span>
                  )}

                  {isViewerCaptain && (
                    <div className="sl-profile-avatar-overlay">
                      {logoUploading ? (
                        <svg className="sl-btn-spinner" width="20" height="20" viewBox="0 0 16 16" fill="none">
                          <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" opacity="0.25" />
                          <path d="M8 2a6 6 0 015.92 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
                          <circle cx="12" cy="13" r="4" />
                        </svg>
                      )}
                    </div>
                  )}
                </div>

                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="sl-profile-avatar-file-input"
                  onChange={handleLogoChange}
                  aria-hidden="true"
                  tabIndex={-1}
                />
                {isViewerCaptain && (
                  <button
                    type="button"
                    className="sl-profile-avatar-change"
                    onClick={handleLogoClick}
                  >
                    Change Logo
                  </button>
                )}
              </div>

              <div className="sl-profile-header-main">
                <div className="sl-profile-header-topline">
                  <h1 className="sl-profile-username">{teamName}</h1>
                  {(header?.teamTag || isViewerCaptain) && (
                    <span className="sl-team-tag-box">
                      {header?.teamTag ? `[${header.teamTag}]` : 'No tag'}
                      {isViewerCaptain && (
                        <button
                          type="button"
                          className="sl-team-tag-box-edit"
                          onClick={openTagModal}
                          title="Edit tag"
                        >
                          &#9998;
                        </button>
                      )}
                    </span>
                  )}
                </div>

                <div className="sl-profile-meta">
                  <span className="sl-profile-meta-item">
                    <span className="sl-profile-meta-strong">{gameSeasonLabel}</span>
                  </span>
                </div>
              </div>

              {/* Right side: ID + status + bio */}
              <div className="sl-profile-header-side">
                <div className="sl-profile-header-idrow">
                  <span className="sl-profile-pill sl-profile-pill--member">
                    Team ID: {header?.teamId ?? numericTeamId}
                  </span>
                  <span
                    className={`sl-profile-status-pill ${
                      isArchived
                        ? 'sl-profile-status-pill--archived'
                        : 'sl-profile-status-pill--active'
                    }`}
                  >
                    {isArchived ? 'Archived' : 'Active'}
                  </span>
                </div>

                {(header?.description || isViewerCaptain) && (
                  <div className="sl-team-bio-box">
                    <p className="sl-team-bio-text">
                      {header?.description || 'No team description yet.'}
                      {isViewerCaptain && (
                        <button
                          type="button"
                          className="sl-team-bio-edit"
                          onClick={openDescriptionModal}
                          title="Edit description"
                        >
                          &#9998;
                        </button>
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Desktop tabs + challenge */}
            <div className="sl-team-tabs-row">
              <div className="sl-profile-tabs">
                {renderTabButton(TABS.OVERVIEW, 'Overview')}
                {renderTabButton(TABS.MATCHES, 'Match History')}
                {renderTabButton(TABS.MANAGEMENT, 'Team Management')}
              </div>
              {challengeInfo?.canChallenge && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleChallengeClick}
                >
                  Challenge Team
                </Button>
              )}
              {!roster.some(m => m.userId === user?.userId) && user && (
                <div className="sl-profile-header-report">
                  <ReportButton
                    targetId={numericTeamId!}
                    reportableContent={[
                      ...(header?.logoUrl && !header?.logoHidden
                        ? [{ type: 'team_logo' as const, label: 'Team Logo' }]
                        : []),
                      { type: 'team_name' as const, label: 'Team Name' },
                      ...(header?.description
                        ? [{ type: 'team_description' as const, label: 'Team Description' }]
                        : []),
                    ]}
                  />
                </div>
              )}
            </div>

            {/* Mobile dropdown */}
            <div className="sl-profile-tabs-mobile">
              <select
                className="sl-profile-tabs-select"
                value={activeTab}
                onChange={(e) => handleTabChange(e.target.value)}
              >
                <option value={TABS.OVERVIEW}>Overview</option>
                <option value={TABS.MATCHES}>Match History</option>
                <option value={TABS.MANAGEMENT}>Team Management</option>
              </select>
            </div>
          </section>

          {/* ---- Tab Content ---- */}
          <div className="sl-profile-tab-content">
            {activeTab === TABS.OVERVIEW && renderOverview()}
            {activeTab === TABS.MATCHES && renderMatchHistory()}
            {activeTab === TABS.MANAGEMENT && renderManagement()}
          </div>
        </div>
      </div>

      {/* ---- Modals ---- */}

      {/* Kick Confirmation */}
      <Modal
        isOpen={!!kickConfirm}
        title="Remove player from team?"
        onClose={() => setKickConfirm(null)}
        onConfirm={handleKickConfirm}
        confirmLabel={actionLoading ? 'Removing...' : 'Remove'}
        disableConfirm={actionLoading}
        disableCancel={actionLoading}
      >
        <p className="sl-profile-muted">
          Are you sure you want to remove{' '}
          <strong style={{ color: '#e5e5e5' }}>{kickConfirm?.username}</strong> from the team?
        </p>
      </Modal>

      {/* Transfer Captain Confirmation */}
      <Modal
        isOpen={isTransferModalOpen}
        title="Confirm captain transfer"
        onClose={() => setIsTransferModalOpen(false)}
        onConfirm={handleTransferConfirm}
        confirmLabel={actionLoading ? 'Transferring...' : 'Confirm Transfer'}
        disableConfirm={actionLoading}
        disableCancel={actionLoading}
      >
        <p className="sl-profile-muted">
          Are you sure you want to transfer the captain role
          {transferTargetId && management?.roster && (
            <>
              {' '}to{' '}
              <strong style={{ color: '#e5e5e5' }}>
                {management.roster.find((m) => m.userId === Number(transferTargetId))?.username}
              </strong>
            </>
          )}
          ?
        </p>
        <p className="sl-profile-muted" style={{ marginTop: '0.5rem' }}>
          This change is permanent unless the new captain decides to transfer the role again.
        </p>
      </Modal>

      {/* Change Team Name */}
      <Modal
        isOpen={isTeamNameModalOpen}
        title="Change Team Name"
        onClose={() => setIsTeamNameModalOpen(false)}
        onConfirm={handleTeamNameConfirm}
        confirmLabel={actionLoading ? 'Saving...' : 'Save'}
        disableConfirm={actionLoading || !teamNameDraft.trim()}
        disableCancel={actionLoading}
      >
        <p className="sl-profile-muted">Enter a new team name.</p>
        <div style={{ marginTop: '0.75rem' }}>
          <input
            type="text"
            className="sl-team-invite-input"
            placeholder="New team name..."
            value={teamNameDraft}
            onChange={(e) =>
              setTeamNameDraft(stripControlChars(e.target.value).slice(0, 32))
            }
            disabled={actionLoading}
          />
        </div>
      </Modal>

      {/* Leave Team Confirmation */}
      <Modal
        isOpen={isLeaveModalOpen}
        title="Leave this team?"
        onClose={() => setIsLeaveModalOpen(false)}
        onConfirm={handleLeaveConfirm}
        confirmLabel={actionLoading ? 'Leaving...' : 'Leave Team'}
        disableConfirm={actionLoading}
        disableCancel={actionLoading}
      >
        <p className="sl-profile-muted">
          Are you sure you want to leave <strong style={{ color: '#e5e5e5' }}>{teamName}</strong>?
          This action cannot be undone.
        </p>
      </Modal>

      {/* Edit Description */}
      <Modal
        isOpen={isDescriptionModalOpen}
        title="Edit Team Description"
        onClose={() => setIsDescriptionModalOpen(false)}
        onConfirm={handleDescriptionConfirm}
        confirmLabel={actionLoading ? 'Saving...' : 'Save'}
        disableConfirm={actionLoading}
        disableCancel={actionLoading}
      >
        <p className="sl-profile-muted">Write a short bio for your team. Leave blank to remove.</p>
        <div style={{ marginTop: '0.75rem' }}>
          <textarea
            className="sl-team-textarea"
            placeholder="Enter a team description..."
            value={descriptionDraft}
            onChange={(e) =>
              setDescriptionDraft(stripControlChars(e.target.value).slice(0, 200))
            }
            disabled={actionLoading}
            rows={3}
            maxLength={200}
          />
          <p className="sl-profile-muted" style={{ fontSize: '0.75rem', marginTop: '0.25rem', textAlign: 'right' }}>
            {descriptionDraft.length}/200
          </p>
        </div>
      </Modal>

      {/* Logo Upload Modal */}
      <Modal
        isOpen={isLogoModalOpen}
        title="Upload Team Logo"
        onClose={() => setIsLogoModalOpen(false)}
        onCancel={() => setIsLogoModalOpen(false)}
        onConfirm={handleLogoModalConfirm}
        confirmLabel="Choose File"
        cancelLabel="Cancel"
      >
        <div style={{ fontSize: '0.88rem', color: '#d4d4d4', lineHeight: 1.6 }}>
          <p style={{ marginBottom: '0.75rem' }}>
            Your team logo will be visible across SALT including leaderboards, match pages, and team profiles. Please review the following requirements before uploading:
          </p>
          <div style={{ background: 'rgba(18, 18, 18, 0.6)', border: '1px solid rgba(163, 163, 163, 0.2)', borderRadius: '0.5rem', padding: '0.75rem 1rem' }}>
            <p style={{ fontWeight: 600, color: '#e5e5e5', marginBottom: '0.5rem' }}>File Requirements</p>
            <p style={{ marginBottom: '0.25rem' }}>Accepted formats: JPEG, PNG, GIF, WebP</p>
            <p style={{ marginBottom: '0.25rem' }}>Maximum file size: 1 MB</p>
            <p style={{ marginBottom: '0.25rem' }}>Maximum dimensions: 500 x 500 px</p>
            <p>Image will be resized to 256 x 256 px</p>
          </div>
          <div style={{ background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.25)', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginTop: '0.65rem' }}>
            <p style={{ fontWeight: 600, color: '#f87171', marginBottom: '0.5rem' }}>Content Policy</p>
            <p>All uploads are automatically scanned for inappropriate content. Images containing nudity, violence, hate symbols, drug references, or other prohibited content will be rejected. Repeated violations may result in account action.</p>
          </div>
        </div>
      </Modal>

      {/* Edit Tag */}
      <Modal
        isOpen={isTagModalOpen}
        title="Edit Team Tag"
        onClose={() => setIsTagModalOpen(false)}
        onConfirm={handleTagConfirm}
        confirmLabel={actionLoading ? 'Saving...' : 'Save'}
        disableConfirm={actionLoading}
        disableCancel={actionLoading}
      >
        <p className="sl-profile-muted">Set a short tag displayed next to your team name (e.g. SLG). Leave blank to remove.</p>
        <div style={{ marginTop: '0.75rem' }}>
          <input
            type="text"
            className="sl-team-invite-input"
            placeholder="TAG"
            value={tagDraft}
            onChange={(e) =>
              setTagDraft(stripControlChars(e.target.value).slice(0, 5).toUpperCase())
            }
            disabled={actionLoading}
            maxLength={5}
            style={{ textTransform: 'uppercase' }}
          />
        </div>
      </Modal>

      {/* Toast Container */}
      <ToastContainer toasts={toast.toasts} onRemove={toast.removeToast} />
    </>
  );
};

export default TeamProfilePage;